import { useEffect, useState } from "react";
import { getCompanies } from "../../repository/data.repository";
import { Company, MAX_PAGE_SIZE } from "../../repository/data.default";
import { QueryOperation } from "../../code/search-query/search-query";
import { nameOf } from "../../utils/typescript";
import { retry } from "../../utils/http";

export interface CompanyScope {
  /** Root (the company the operator opened) + every descendant in the parentCompanyUid tree. The order
   *  matters: the root is always first so pickers can preselect it. */
  readonly companies: Company[];
  /** Just the uids, in the same order — convenience for `companyUid IN scope` queries. */
  readonly uids: string[];
  readonly loading: boolean;
}

/**
 * Walks the parent → children chain breadth-first and returns the root plus every descendant company.
 * One request per depth level: a holding with 50 direct subs and no grandchildren is TWO requests
 * total (seed + level 1). Keeps the Companies-grid-style IN filters that back every panel's list and
 * counter honest without needing a new "give me the whole subtree" backend endpoint.
 */
export function useCompanyScope(
  rootUid: string | undefined,
  rootCompany?: Company | null,
  reloadToken?: string | number,
): CompanyScope {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!rootUid) {
      setCompanies([]);
      return;
    }
    let aborted = false;
    setLoading(true);
    (async () => {
      const collected: Company[] = rootCompany ? [rootCompany] : [];
      // If we weren't handed the root company object, pull it first — pickers need its name.
      if (!rootCompany) {
        try {
          const seed = await retry(() =>
            getCompanies({
              searchQuery: {
                conditions: [
                  { field: nameOf<Company>("uid"), values: [rootUid], operation: QueryOperation.Equal },
                ],
              },
              pageNumber: 0,
              pageSize: 1,
            }),
          );
          if (seed.data[0]) collected.push(seed.data[0]);
        } catch {
          /* fall through — descendants fetch will still try */
        }
      }

      let frontier: string[] = [rootUid];
      // Safety cap on depth to avoid pathological loops in the tree.
      for (let depth = 0; depth < 20 && frontier.length > 0; depth++) {
        // Snapshot the current frontier into a loop-local const so the retry callback can't close over
        // the mutating `let frontier` — eslint's no-loop-func is satisfied, and semantics are unchanged
        // because we await the request before reassigning.
        const currentFrontier = frontier;
        try {
          const result = await retry(() =>
            getCompanies({
              searchQuery: {
                conditions: [
                  {
                    field: nameOf<Company>("parentCompanyUid"),
                    values: currentFrontier,
                    operation: QueryOperation.In,
                  },
                ],
              },
              pageNumber: 0,
              pageSize: MAX_PAGE_SIZE,
            }),
          );
          const next = result.data;
          collected.push(...next);
          frontier = next.map((c) => c.uid);
        } catch {
          break;
        }
      }

      if (!aborted) {
        setCompanies(collected);
        setLoading(false);
      }
    })();

    return () => {
      aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootUid, rootCompany?.uid, reloadToken]);

  return { companies, uids: companies.map((c) => c.uid), loading };
}
